import { makeVar } from "@apollo/client";
import {
  DigitalGiftInput,
  DigitalGiftType,
  ItemInput,
  ItemType
} from "@shared/graphql.types";
import { getDerivedLocale } from "@shared/utils.locale";

import { activeMemberships } from "./constants";
import { ActiveMembershipIdsType, ExtendedUserType, User } from "./types";

export const guestUser: ExtendedUserType = {
  basket: [],
  carriedForward: 0,
  discounts: [],
  email: "guest@bp.com",
  hints: [],
  id: "initial",
  name: "guest",
  firstName: "",
  lastName: "",
  reviews: [],
  spentSoFar: 0,
  wishlist: [],
  includedDiscounts: [],
  rejectedDiscounts: [],
  isFreeTrialAllowed: true,
  isLoggedIn: false,
  fullName: ""
};

//TODO move to a seperate package
const getMembershipIds = (): ActiveMembershipIdsType => {
  return activeMemberships[getDerivedLocale()];
};

//TODO move to a seperate package
const toDigitalGiftInput = (
  gift: DigitalGiftType | null
): DigitalGiftInput | null => {
  if (!gift) return null;

  return {
    ...gift,
    __typename: undefined
  } as DigitalGiftInput;
};

//TODO move to a seperate package
const toItemInput = (item: ItemType): ItemInput => {
  if (!item) throw new Error(`toItemInput(): received null item`);

  return {
    id: item.id,
    type: item.type,
    membershipId: item.membership?.id ?? null,
    variantId: item.variant?.id ?? null,
    variantSku: item.variant?.sku ?? null,
    quantity: item.quantity,
    giftInfo: toDigitalGiftInput(item.giftInfo ?? null),
    origin: item?.origin ?? "user"
  };
};

export const BPUser = (user: ExtendedUserType): User => {
  return {
    ...(user || {}),
    get fullName(): string {
      return [user?.firstName, user?.lastName].join(" ").trim();
    },
    get basketItemInputs(): ItemInput[] {
      return user?.basket?.map(toItemInput) || [];
    },
    get wishlistItemInputs(): ItemInput[] {
      return user?.wishlist?.map(toItemInput) || [];
    },
    get basketCount(): number {
      return (
        user?.basket?.reduce((acc, item) => {
          return item?.variant?.availability?.status === "instock" ||
            item?.variant === null
            ? acc + item.quantity
            : acc;
        }, 0) || 0
      );
    },
    get isMember(): boolean {
      const email = user?.email;
      const membership = user?.membership;
      return !!(
        membership &&
        (membership.id !== "none:none" || email?.endsWith("@test.com"))
      );
    },
    get hasMembershipInBag(): boolean {
      return user?.basket?.some(({ type }) => type === "membership");
    },
    get hasGiftMembershipInBag(): boolean {
      return user?.basket?.some(({ type }) => type === "giftMembership");
    },
    get hasMembership(): boolean {
      return this.isMember || this.hasMembershipInBag;
    },
    get effectiveMembership() {
      if (user.membership?.id !== "none:none") {
        return user.membership;
      }
      return user?.basket?.find(({ type }) => type === "membership")
        ?.membership;
    },
    get hasFreeTrialMembership(): boolean {
      const {
        freeTrialMembershipId,
        freeTrialMembership30Id,
        freeTrialLimitlessMembership30Id,
        threeMonthFreeTrialMembershipId,
        thirtyDayFreeTrialMembershipId,
        thirtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToAnnualMembershipId
      } = getMembershipIds();
      const id = this.effectiveMembership?.id;
      return !!(
        id &&
        [
          freeTrialMembershipId,
          freeTrialMembership30Id,
          freeTrialLimitlessMembership30Id,
          threeMonthFreeTrialMembershipId,
          thirtyDayFreeTrialMembershipId,
          thirtyDayFreeTrialRenewsToMonthlyMembershipId,
          sixtyDayFreeTrialRenewsToMonthlyMembershipId,
          sixtyDayFreeTrialRenewsToAnnualMembershipId
        ].includes(id)
      );
    },
    get hasFreeTrialMembershipInBag(): boolean {
      const {
        freeTrialMembershipId,
        freeTrialMembership30Id,
        freeTrialLimitlessMembership30Id,
        threeMonthFreeTrialMembershipId,
        thirtyDayFreeTrialMembershipId,
        thirtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToMonthlyMembershipId,
        sixtyDayFreeTrialRenewsToAnnualMembershipId
      } = getMembershipIds();
      return user?.basket?.some(
        (item) =>
          item.type === "membership" &&
          item.membership?.id &&
          [
            freeTrialMembershipId,
            freeTrialMembership30Id,
            freeTrialLimitlessMembership30Id,
            threeMonthFreeTrialMembershipId,
            thirtyDayFreeTrialMembershipId,
            thirtyDayFreeTrialRenewsToMonthlyMembershipId,
            sixtyDayFreeTrialRenewsToMonthlyMembershipId,
            sixtyDayFreeTrialRenewsToAnnualMembershipId
          ].includes(item.membership?.id)
      );
    },
    get hasPlusMembership(): boolean {
      return (
        !!this.effectiveMembership?.name?.toUpperCase().includes("PLUS") ||
        !!this.effectiveMembership?.id?.toUpperCase().includes("PLUS")
      );
    },
    get hasPlusMembershipInBag(): boolean {
      return user?.basket?.some(
        (item) =>
          item.type === "membership" &&
          item.membership?.name?.toUpperCase().includes("PLUS")
      );
    },
    get hasStaffMembership() {
      const { staffMembershipId } = getMembershipIds();
      return (
        user.membership?.id === staffMembershipId ||
        !!this.effectiveMembership?.id.toUpperCase().includes("STAFF")
      );
    }
  };
};

export const getUser = (): User => {
  if (typeof localStorage === "undefined") return BPUser(guestUser);
  const x = localStorage.getItem("user");
  return BPUser(x ? JSON.parse(x) : guestUser);
};

export const userVar = makeVar<User>(getUser());

export * from "./types";
